import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ViewChild,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { ButtonModule } from 'primeng/button'
import { ChipModule } from 'primeng/chip'
import { TooltipModule } from 'primeng/tooltip'
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { firstValueFrom } from 'rxjs'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { AssistanceAttachment, AssistanceAttachmentsRole } from '../../../models/assistance-attachment.model'
import { AssistanceAttachmentsService } from '../../../services/domain/assistance-attachments.service'
import { FileUploadService } from '../../../services/file-upload.service'
import { FileViewerService } from '../../../services/fileViewer/file-viewer.service'
import { ALLOWED_DOCUMENT_ACCEPT, isAllowedDocumentType } from './allowed-document-types.const'
import { PrimeNgToastService } from '../../../services/prime-ng-toast/prime-ng-toast.service'
import {
  DocumentSelectModalComponent,
  type DocumentSelectModalData,
} from './document-select-modal/document-select-modal.component'

/** A queued, not yet persisted attachment change. */
interface PendingAdd {
  /** Negative synthetic id so it stays unique and distinguishable from committed junction ids. */
  tempId: number
  kind: 'new' | 'existing'
  name: string
  /** For `existing` picks: the client document to link. */
  documentId?: number
  /** For `existing` picks: the document url (so the chip can still be opened before save). */
  url?: string
  /** For `new` uploads: the raw file, held in memory until flush (uploaded only on save). */
  file?: File
  /** MIME type of the file, sent to the backend on flush for server-side validation. */
  mimeType?: string
}

/** Display shape for a single chip — either a committed attachment or a pending add. */
interface AttachmentItem {
  id: number
  name: string
  /** `null` for a queued new upload (no url until it is uploaded on save). */
  url: string | null
  isPending: boolean
}

/**
 * List of documents (PDF / images) attached to the detailed description of an assistance.
 *
 * In editable mode (`readonly = false`, coordinator) new files can be uploaded or existing
 * client documents attached, and attachments can be detached again. In readonly mode
 * (assistant) the attachments can only be opened (PDF viewer) or downloaded.
 *
 * Editing is deferred: adding/removing only mutates local pending state and emits `changed`.
 * Nothing is persisted until the host editor saves the assistance and calls {@link flush},
 * so attachments are saved together with the rest of the assistance. This also lets a brand-new
 * (not yet saved) assistance accumulate uploads that are flushed once the first save assigns an id.
 */
@Component({
  selector: 'app-assistance-attachments',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule, ChipModule, TooltipModule, DynamicDialogModule, TranslocoModule],
  providers: [DialogService],
  templateUrl: './assistance-attachments.component.html',
  styleUrls: ['./assistance-attachments.component.scss'],
})
export class AssistanceAttachmentsComponent {
  private readonly attachmentsService = inject(AssistanceAttachmentsService)
  private readonly fileUploadService = inject(FileUploadService)
  private readonly fileViewerService = inject(FileViewerService)
  private readonly dialogService = inject(DialogService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly translocoService = inject(TranslocoService)
  private readonly primeNgToastService = inject(PrimeNgToastService)

  @ViewChild('fileInput') private fileInput: ElementRef<HTMLInputElement>

  readonly assistanceId = input<number | null>(null)
  readonly readonly = input<boolean>(false)
  readonly role = input<AssistanceAttachmentsRole>('coordinator')

  /** Emitted whenever the pending state changes, so the host editor can mark itself dirty. */
  @Output() readonly changed = new EventEmitter<void>()

  /** Server state — attachments already persisted for the assistance. */
  private readonly committed = signal<AssistanceAttachment[]>([])
  /** Queued additions (new uploads + picked client documents), not yet persisted. */
  private readonly pendingAdds = signal<PendingAdd[]>([])
  /** Junction ids of committed attachments queued for detaching, not yet persisted. */
  private readonly pendingRemovals = signal<Set<number>>(new Set())
  /** Bumped to force a reload of {@link committed} (e.g. after a flush). */
  private readonly refreshTrigger = signal(0)

  private nextTempId = -1
  private previousAssistanceId: number | null | undefined = undefined

  readonly workInProgress = signal(false)
  readonly allowedAccept = ALLOWED_DOCUMENT_ACCEPT

  /** Committed (minus queued removals) merged with queued additions, sorted by name. */
  readonly attachments = computed<AttachmentItem[]>(() => {
    const removed = this.pendingRemovals()
    const committedItems: AttachmentItem[] = this.committed()
      .filter((a) => !removed.has(a.id))
      .map((a) => ({ id: a.id, name: a.name, url: a.url, isPending: false }))
    const pendingItems: AttachmentItem[] = this.pendingAdds().map((p) => ({
      id: p.tempId,
      name: p.name,
      url: p.url ?? null,
      isPending: true,
    }))
    return [...committedItems, ...pendingItems].sort((x, y) => AssistanceAttachment.intlSortComparer(x.name, y.name))
  })

  constructor() {
    effect((onCleanup) => {
      const assistanceId = this.assistanceId()
      this.refreshTrigger()

      // Switching to a different assistance discards any pending changes from the previous one
      // (the null -> new-id transition right after the first save is excluded — flush already ran).
      if (this.previousAssistanceId !== undefined && this.previousAssistanceId !== assistanceId) {
        const switchedFromExisting = this.previousAssistanceId !== null
        if (switchedFromExisting) this.clearPending()
      }
      this.previousAssistanceId = assistanceId

      if (assistanceId == null) {
        this.committed.set([])
        return
      }
      const sub = this.attachmentsService
        .find(assistanceId, this.role())
        .subscribe((attachments) => this.committed.set(attachments))
      onCleanup(() => sub.unsubscribe())
    })
  }

  /** Whether there are queued changes waiting to be persisted by {@link flush}. */
  hasPendingChanges(): boolean {
    return this.pendingAdds().length > 0 || this.pendingRemovals().size > 0
  }

  /**
   * Persists the queued changes against the given (now known) assistance id: applies detaches,
   * links picked client documents, and uploads + attaches queued files. Called by the host editor
   * after the assistance itself has been saved.
   */
  async flush(assistanceId: number): Promise<void> {
    if (!this.hasPendingChanges()) return

    this.workInProgress.set(true)
    try {
      for (const id of this.pendingRemovals()) {
        await this.attachmentsService.remove(id)
      }
      for (const add of this.pendingAdds()) {
        if (add.kind === 'existing' && add.documentId != null) {
          await this.attachmentsService.attachExisting(assistanceId, add.documentId)
        } else if (add.kind === 'new' && add.file) {
          const uploaded = await this.fileUploadService.upload(add.file)
          await this.attachmentsService.attachNew(
            assistanceId,
            FileUploadService.parseFileNameFromUrl(uploaded.id),
            uploaded.id,
            add.mimeType,
          )
        }
      }
      this.clearPending()
      this.refreshTrigger.update((n) => n + 1)
    } finally {
      this.workInProgress.set(false)
    }
  }

  async openAttachment(item: AttachmentItem): Promise<void> {
    if (!item.url) return
    this.workInProgress.set(true)
    try {
      await this.fileViewerService.viewIfPdfElseDownload(item.url)
    } finally {
      this.workInProgress.set(false)
    }
  }

  selectFile(): void {
    this.fileInput.nativeElement.click()
  }

  onFileInput(): void {
    const files = this.fileInput.nativeElement.files
    if (!files || files.length === 0) return

    const file = files[0]
    this.fileInput.nativeElement.value = ''

    if (!isAllowedDocumentType(file)) {
      this.primeNgToastService.displayCustomToast('error', 'ASSISTANCE_ATTACHMENTS.INVALID_FILE_TYPE')
      return
    }

    this.pendingAdds.update((adds) => [
      ...adds,
      { tempId: this.nextTempId--, kind: 'new', name: file.name, file, mimeType: file.type },
    ])
    this.changed.emit()
  }

  async openDocumentPicker(): Promise<void> {
    const assistanceId = this.assistanceId()
    if (assistanceId == null) return

    const documents = await firstValueFrom(this.attachmentsService.findAvailable(assistanceId))
    const pendingDocumentIds = new Set(
      this.pendingAdds()
        .filter((p) => p.kind === 'existing')
        .map((p) => p.documentId),
    )
    const available = documents.filter((d) => !pendingDocumentIds.has(d.documentId))

    const data: DocumentSelectModalData = { documents: available }
    const ref = this.dialogService.open(DocumentSelectModalComponent, {
      header: this.translocoService.translate('ASSISTANCE_ATTACHMENTS.PICK_FROM_DOCUMENTS'),
      closable: true,
      draggable: false,
      modal: true,
      baseZIndex: 100000, // Solving issue with focus input in modal
      autoZIndex: true, // Solving issue with focus input in modal
      appendTo: document.querySelector('ion-modal') ?? 'body', // Solving issue with focus input in modal
      data,
    })

    ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selected: AssistanceAttachment[] | null) => {
      if (!selected?.length) return
      this.pendingAdds.update((adds) => [
        ...adds,
        ...selected.map(
          (d): PendingAdd => ({
            tempId: this.nextTempId--,
            kind: 'existing',
            name: d.name,
            url: d.url,
            documentId: d.documentId,
          }),
        ),
      ])
      this.changed.emit()
    })
  }

  removeAttachment(event: Event, item: AttachmentItem): void {
    event.stopPropagation()
    if (item.isPending) {
      this.pendingAdds.update((adds) => adds.filter((p) => p.tempId !== item.id))
    } else {
      this.pendingRemovals.update((set) => new Set(set).add(item.id))
    }
    this.changed.emit()
  }

  attachmentIcon(item: AttachmentItem): string {
    return item.name.toLowerCase().endsWith('.pdf') ? 'pi pi-file-pdf' : 'pi pi-image'
  }

  onChipClick(event: Event, item: AttachmentItem): void {
    if ((event.target as Element).closest('button')) return
    void this.openAttachment(item)
  }

  private clearPending(): void {
    this.pendingAdds.set([])
    this.pendingRemovals.set(new Set())
  }
}
